import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Types for the config diff state
interface RowData {
  id?: number | string;
  asset_id?: string;
  timeStamp?: string;
  assetName?: string;
  file_id?: string;
  security_score?: number;
  device_id?: string;
  previous_file?: any;
  date_added?: any;
  vendor?: string;
  model?: string;
  version?: string;
  [key: string]: any;
}

interface ConfigDiffState {
  // Single row data for show diff
  rowData: RowData | null;
  // Selected rows for comparison
  selectedRows: RowData[] | null;
  // Device ID
  device_id: string | null;
  // Click source identifier
  click: string | null;
  // Navigation source: tracks which view triggered navigation to show-diff
  // "assets-page"       => came from Asset Management (single-asset mode)
  // "configdiff-sidebar" => came from the full sidebar ConfigDiff page
  source: "assets-page" | "configdiff-sidebar" | null;
}

const initialState: ConfigDiffState = {
  rowData: null,
  selectedRows: null,
  device_id: null,
  click: null,
  source: null,
};

const configDiffSlice = createSlice({
  name: "configDiff",
  initialState,
  reducers: {
    // Set single row data for show diff
    setConfigDiffRowData(state, action: PayloadAction<RowData | null>) {
      state.rowData = action.payload;
      state.selectedRows = null; // Clear selected rows when setting single row
    },
    // Set selected rows for comparison
    setConfigDiffSelectedRows(state, action: PayloadAction<RowData[] | null>) {
      state.selectedRows = action.payload;
      state.rowData = null; // Clear single row when setting comparison rows
    },
    // Set device ID
    setConfigDiffDeviceId(state, action: PayloadAction<string | null>) {
      state.device_id = action.payload;
    },
    // Set click source
    setConfigDiffClick(state, action: PayloadAction<string | null>) {
      state.click = action.payload;
    },
    // Set navigation source
    setConfigDiffSource(state, action: PayloadAction<ConfigDiffState['source']>) {
      state.source = action.payload;
    },
    // Set all config diff data at once (for navigation)
    setConfigDiffData(
      state,
      action: PayloadAction<{
        rowData?: RowData | null;
        selectedRows?: RowData[] | null;
        device_id?: string | null;
        click?: string | null;
        source?: ConfigDiffState['source'];
      }>
    ) {
      if (action.payload.rowData !== undefined) {
        state.rowData = action.payload.rowData;
      }
      if (action.payload.selectedRows !== undefined) {
        state.selectedRows = action.payload.selectedRows;
      }
      if (action.payload.device_id !== undefined) {
        state.device_id = action.payload.device_id;
      }
      if (action.payload.click !== undefined) {
        state.click = action.payload.click;
      }
      if (action.payload.source !== undefined) {
        state.source = action.payload.source;
      }
    },
    // Clear all config diff data
    clearConfigDiffData(state) {
      state.rowData = null;
      state.selectedRows = null;
      state.device_id = null;
      state.click = null;
      state.source = null;
    },
  },
});

export const {
  setConfigDiffRowData,
  setConfigDiffSelectedRows,
  setConfigDiffDeviceId,
  setConfigDiffClick,
  setConfigDiffSource,
  setConfigDiffData,
  clearConfigDiffData,
} = configDiffSlice.actions;

export default configDiffSlice.reducer;
